import React, { useState, useMemo, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import toast from 'react-hot-toast';
import {
  LayoutDashboard,
  CheckSquare,
  GitPullRequest,
  AlertOctagon,
  Target,
  ChevronDown,
  Check,
  Clipboard,
  ThumbsUp,
  ThumbsDown,
  CalendarClock,
  Pencil,
  Save,
  UserCircle2,
  ArrowUpRight,
  BarChart3,
} from 'lucide-react';

import {
  patchMeetingJiraUpdate,
  patchUserConfiguredMeetingJiraUpdate,
  scheduleMeetingFollowups,
  scheduleUserConfiguredMeetingFollowups,
  executeMeetingJiraSync,
  executeMeetingTaskCreation,
} from '../../services/workflowApi';

// ─── HELPER COMPONENTS ─────────────────────────────────────────────────────────

// Date urgency helper for follow-up dates
const getDateUrgency = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
  if (diff < 0)
    return {
      label: 'Overdue',
      color: 'text-red-400',
      bg: 'bg-red-500/15',
      border: 'border-red-500/20',
    };
  if (diff === 0)
    return {
      label: 'Today',
      color: 'text-amber-400',
      bg: 'bg-amber-500/15',
      border: 'border-amber-500/20',
    };
  if (diff <= 2)
    return {
      label: `In ${diff}d`,
      color: 'text-amber-300',
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/15',
    };
  return {
    label: `In ${diff}d`,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/15',
  };
};

const DateBadge = ({ dateStr }) => {
  const urgency = getDateUrgency(dateStr);
  if (!urgency) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${urgency.color} ${urgency.bg}`}
    >
      <CalendarClock className="h-3 w-3" />
      {urgency.label}
    </span>
  );
};

// Copy Button Component
const CopyButton = ({ textToCopy }) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(textToCopy).then(() => {
      setIsCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      className="rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
      title="Copy to clipboard"
    >
      {isCopied ? (
        <Check className="h-3.5 w-3.5 text-emerald-400" />
      ) : (
        <Clipboard className="h-3.5 w-3.5" />
      )}
    </button>
  );
};

// Badge Component (Tailwind version)
const Badge = ({ children, colorClass, bgClass }) => (
  <span
    className={`rounded px-2 py-0.5 text-[11px] font-semibold tracking-wide ${colorClass || 'text-slate-400'} ${bgClass || 'bg-slate-500/10'}`}
  >
    {children}
  </span>
);

// Impact Dot Component
const ImpactDot = ({ level }) => {
  const colorMap = {
    high: 'bg-red-500',
    medium: 'bg-amber-500',
    low: 'bg-green-500',
  };
  return (
    <span
      className={`h-2 w-2 flex-shrink-0 rounded-full ${colorMap[level] || 'bg-slate-500'}`}
    />
  );
};

// Approve/Reject Button Component
const ApproveButton = ({ id, approvedSet, onApprove, onReject }) => {
  const approved = approvedSet.has(id);
  const rejected = approvedSet.has(id + '_rejected');

  if (approved) {
    return (
      <span className="flex items-center gap-1 text-xs font-semibold text-green-400">
        <ThumbsUp className="h-3.5 w-3.5" />
        Approved
      </span>
    );
  }
  if (rejected) {
    return (
      <span className="flex items-center gap-1 text-xs font-semibold text-red-400">
        <ThumbsDown className="h-3.5 w-3.5" />
        Rejected
      </span>
    );
  }
  return (
    <div className="flex gap-1.5">
      <button
        onClick={() => onApprove(id)}
        className="cursor-pointer rounded border-none bg-green-500/15 px-3 py-1 text-xs font-semibold text-green-400 transition-all hover:bg-green-500/25"
      >
        Approve
      </button>
      <button
        onClick={() => onReject(id)}
        className="cursor-pointer rounded border-none bg-red-500/10 px-3 py-1 text-xs font-semibold text-red-400 transition-all hover:bg-red-500/20"
      >
        Reject
      </button>
    </div>
  );
};

// ─── OVERVIEW TAB ──────────────────────────────────────────────────────────────

const OverviewTab = ({ data }) => {
  const stats = useMemo(() => {
    const tasks = data?.task_wise_summary || [];
    const blockers = data?.unique_blockers || [];

    const blockedCount = tasks.filter((t) => t.is_blocked_in_meeting).length;
    const highImpactBlockers = blockers.filter(
      (b) => b.classification?.impact_level === 'high',
    ).length;
    const mediumImpactBlockers = blockers.filter(
      (b) => b.classification?.impact_level === 'medium',
    ).length;
    const lowImpactBlockers = blockers.filter(
      (b) => b.classification?.impact_level === 'low',
    ).length;

    const positiveSentiment = tasks.filter(
      (t) => t.sentiment === 'positive',
    ).length;
    const negativeSentiment = tasks.filter(
      (t) => t.sentiment === 'negative',
    ).length;
    const neutralSentiment = tasks.filter(
      (t) => t.sentiment === 'neutral',
    ).length;

    return {
      totalTasks: tasks.length,
      blockedCount,
      highImpactBlockers,
      mediumImpactBlockers,
      lowImpactBlockers,
      positiveSentiment,
      negativeSentiment,
      neutralSentiment,
    };
  }, [data]);

  const memberWorkload = useMemo(() => {
    const members = data?.member_wise_updates || [];
    return members
      .map((m) => ({
        name: m.speaker_name || 'Unknown',
        role: m.role || '',
        sentiment: m.sentiment,
        issueCount: m.mentioned_issue_keys?.length || 0,
      }))
      .sort((a, b) => b.issueCount - a.issueCount);
  }, [data]);

  const maxIssues = Math.max(...memberWorkload.map((m) => m.issueCount), 1);

  return (
    <div className="flex min-w-0 flex-col gap-5">
      {/* Meeting Summary Header */}
      {data?.overall_summary && (
        <div className="rounded-xl border border-blue-500/15 bg-gradient-to-br from-blue-500/5 to-violet-500/5 p-4 sm:p-5">
          <div className="mb-3 flex items-start justify-between gap-3">
            <h4 className="text-sm font-semibold text-zinc-300">
              Overall Summary
            </h4>
            <CopyButton textToCopy={data.overall_summary} />
          </div>
          <div className="prose prose-sm prose-invert max-w-none break-words text-zinc-300">
            <ReactMarkdown>{data.overall_summary}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-4">
          <div className="text-3xl font-extrabold leading-none text-red-400">
            {stats.highImpactBlockers}
          </div>
          <div className="mt-1.5 text-xs font-medium text-zinc-500">
            High-Impact Blockers
          </div>
        </div>
        <div className="rounded-lg border border-blue-500/15 bg-blue-500/5 p-4">
          <div className="text-3xl font-extrabold leading-none text-blue-400">
            {stats.totalTasks}
          </div>
          <div className="mt-1.5 text-xs font-medium text-zinc-500">
            Total Tasks
          </div>
        </div>
        <div className="rounded-lg border border-amber-500/15 bg-amber-500/5 p-4">
          <div className="text-3xl font-extrabold leading-none text-amber-400">
            {stats.blockedCount}
          </div>
          <div className="mt-1.5 text-xs font-medium text-zinc-500">
            Blocked Tasks
          </div>
        </div>
        <div className="rounded-lg border border-green-500/15 bg-green-500/5 p-4">
          <div className="text-3xl font-extrabold leading-none text-green-400">
            {stats.positiveSentiment}
          </div>
          <div className="mt-1.5 text-xs font-medium text-zinc-500">
            Positive Sentiment
          </div>
        </div>
      </div>

      {/* Member Workload */}
      {memberWorkload.length > 0 && (
        <div className="rounded-xl border border-zinc-700/30 bg-zinc-900/40 p-4 sm:p-5">
          <div className="mb-4 text-xs font-bold uppercase tracking-widest text-zinc-500">
            Member Workload
          </div>
          <div className="flex flex-col gap-2.5">
            {memberWorkload.map((member, idx) => (
              <div
                key={idx}
                className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3"
              >
                <div className="w-full flex-shrink-0 truncate text-sm font-medium text-zinc-300 sm:w-32">
                  {member.name}
                </div>
                <div className="h-1.5 flex-1 overflow-hidden rounded bg-zinc-700/30">
                  <div
                    className="h-full rounded bg-gradient-to-r from-blue-500 to-indigo-500"
                    style={{
                      width: `${(member.issueCount / maxIssues) * 100}%`,
                    }}
                  />
                </div>
                <div className="w-full text-left text-xs text-zinc-500 sm:w-6 sm:text-right">
                  {member.issueCount}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── ACTION ITEMS TAB ──────────────────────────────────────────────────────────

const ActionItemsTab = ({
  data,
  approved,
  onApprove,
  onReject,
  meetingContext,
}) => {
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('all');
  const [isCreating, setIsCreating] = useState(false);

  const actionItems = useMemo(() => data?.extracted_action_items || [], [data]);

  const getBackendActionId = (item, idx) =>
    `action:${item.description || ''}:${item.owner_email || ''}:${item.due_description || ''}:${idx}`;

  const owners = useMemo(() => {
    const uniqueOwners = [
      ...new Set(
        actionItems.map(
          (a) => a.owner_email || a.owner_role_or_team || 'Unassigned',
        ),
      ),
    ];
    return ['all', ...uniqueOwners];
  }, [actionItems]);

  const filtered = useMemo(() => {
    if (filter === 'all') return actionItems;
    return actionItems.filter(
      (a) => (a.owner_email || a.owner_role_or_team) === filter,
    );
  }, [actionItems, filter]);

  const approvedCount = actionItems.filter((a, i) =>
    approved.has(a.description + (a.owner_email || i)),
  ).length;

  const handleCreateTasks = async () => {
    if (!meetingContext?.accessToken || !meetingContext?.eventId) {
      toast.error('Missing context for task creation.');
      return;
    }
    const approvedIds = actionItems
      .map((item, i) => ({ item, i }))
      .filter(({ item, i }) =>
        approved.has(item.description + (item.owner_email || i)),
      )
      .map(({ item, i }) => getBackendActionId(item, i));

    if (approvedIds.length === 0) {
      toast.error('Approve at least one action item first.');
      return;
    }
    setIsCreating(true);
    try {
      const res = await executeMeetingTaskCreation(
        meetingContext.accessToken,
        meetingContext.eventId,
        meetingContext.isManual,
        approvedIds,
      );
      toast.success(
        `Created ${res?.success_count ?? 0} of ${res?.attempted_count ?? 0} Jira tasks.`,
      );
      // Surface blocker warnings for successfully created tasks
      const warnings = (res?.results || []).flatMap((r) => r.warnings || []);
      if (warnings.length > 0) {
        warnings.forEach((w) =>
          toast(`⚠ ${w}`, { icon: '🚧', duration: 6000 }),
        );
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to create Jira tasks.');
    } finally {
      setIsCreating(false);
    }
  };

  if (actionItems.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-zinc-500">
        No action items found.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <div className="flex items-center gap-3">
          <div className="text-xs text-zinc-500">
            <span className="font-bold text-green-400">{approvedCount}</span> of{' '}
            {actionItems.length} approved
          </div>
          {approvedCount > 0 && meetingContext?.accessToken && (
            <button
              onClick={handleCreateTasks}
              disabled={isCreating}
              className="flex items-center gap-1.5 rounded-lg bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-400 transition-colors hover:bg-emerald-500/25 disabled:opacity-50"
            >
              {isCreating
                ? 'Creating…'
                : `Create ${approvedCount} Jira Task${approvedCount > 1 ? 's' : ''}`}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {owners.map((o) => (
            <button
              key={o}
              onClick={() => setFilter(o)}
              className={`cursor-pointer rounded-full border-none px-3 py-1 text-xs transition-all ${
                filter === o
                  ? 'bg-blue-500/20 font-semibold text-blue-300'
                  : 'bg-zinc-700/20 font-normal text-zinc-500 hover:bg-zinc-700/30'
              }`}
            >
              {o === 'all' ? 'All' : o.split('@')[0]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {filtered.map((item, idx) => {
          const itemId = item.description + (item.owner_email || idx);
          const isExpanded = expanded === itemId;
          const isApproved = approved.has(itemId);
          const isRejected = approved.has(itemId + '_rejected');

          return (
            <div
              key={idx}
              className={`overflow-hidden rounded-lg border transition-colors ${
                isApproved
                  ? 'border-green-500/20 bg-green-500/5'
                  : isRejected
                    ? 'border-red-500/15 bg-red-500/5'
                    : 'border-zinc-700/20 bg-zinc-900/50'
              }`}
            >
              {/* eslint-disable-next-line */}
              <div
                onClick={() => setExpanded(isExpanded ? null : itemId)}
                className="flex cursor-pointer items-center gap-3 p-3.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    {item.linked_entities?.issue_keys?.length > 0 && (
                      <Badge
                        colorClass="text-blue-400"
                        bgClass="bg-blue-500/10"
                      >
                        {item.linked_entities.issue_keys[0]}
                      </Badge>
                    )}
                    <span className="truncate text-sm font-medium text-zinc-300">
                      {item.description}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-zinc-500">
                    <span>
                      {item.owner_email ||
                        item.owner_role_or_team ||
                        'Unassigned'}
                    </span>
                    {item.due_description && (
                      <>
                        <span className="text-zinc-600">·</span>
                        <span className="text-zinc-500">
                          {item.due_description}
                        </span>
                      </>
                    )}
                    {item.follow_up_date && (
                      <>
                        <span className="text-zinc-600">·</span>
                        <DateBadge dateStr={item.follow_up_date} />
                      </>
                    )}
                  </div>
                </div>
                {/* eslint-disable-next-line */}
                <div onClick={(e) => e.stopPropagation()}>
                  <ApproveButton
                    id={itemId}
                    approvedSet={approved}
                    onApprove={onApprove}
                    onReject={onReject}
                  />
                </div>
                <ChevronDown
                  className={`h-3.5 w-3.5 flex-shrink-0 text-zinc-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </div>
              {isExpanded && (
                <div className="border-t border-zinc-700/10 px-4 pb-4 pt-0">
                  <div className="space-y-1 pt-3 text-xs text-zinc-500">
                    <div>
                      <span className="font-medium text-zinc-400">Owner:</span>{' '}
                      {item.owner_email ||
                        item.owner_role_or_team ||
                        'Unassigned'}
                    </div>
                    {item.source_speaker_email && (
                      <div className="flex items-center gap-1.5">
                        <UserCircle2 className="h-3 w-3 text-zinc-400" />
                        <span className="font-medium text-zinc-400">
                          Assigned by:
                        </span>{' '}
                        <span className="text-violet-400">
                          {item.source_speaker_email}
                        </span>
                      </div>
                    )}
                    {item.action_type && (
                      <div>
                        <span className="font-medium text-zinc-400">Type:</span>{' '}
                        <span className="rounded bg-zinc-500/10 px-2 py-0.5 text-[11px] font-semibold text-zinc-300">
                          {item.action_type.replace(/_/g, ' ')}
                        </span>
                      </div>
                    )}
                    {item.follow_up_date && (
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-zinc-400">
                          Follow-up:
                        </span>{' '}
                        <DateBadge dateStr={item.follow_up_date} />
                        <span className="text-zinc-500">
                          {item.follow_up_date}
                        </span>
                      </div>
                    )}
                    {item.linked_entities?.issue_keys?.length > 0 && (
                      <div>
                        <span className="font-medium text-zinc-400">
                          Linked Issues:
                        </span>{' '}
                        {item.linked_entities.issue_keys.join(', ')}
                      </div>
                    )}
                    {item.due_description && (
                      <div>
                        <span className="font-medium text-zinc-400">Due:</span>{' '}
                        {item.due_description}
                      </div>
                    )}
                  </div>
                  {isApproved && (
                    <div className="mt-2.5 text-xs italic text-green-400">
                      ✓ This item is approved and queued for agent execution.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── JIRA UPDATES TAB ──────────────────────────────────────────────────────────

const JiraUpdatesTab = ({
  data,
  approved,
  onApprove,
  onReject,
  meetingContext,
}) => {
  const [expanded, setExpanded] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Flatten all proposed_jira_issue_updates from all task_wise_summary items
  const jiraUpdates = useMemo(() => {
    const tasks = data?.task_wise_summary || [];
    const updates = [];
    tasks.forEach((task) => {
      if (task.proposed_jira_issue_updates?.length > 0) {
        task.proposed_jira_issue_updates.forEach((update) => {
          updates.push({
            ...update,
            task_issue_key: task.issue_key,
          });
        });
      }
    });
    return updates;
  }, [data]);

  const approvedCount = jiraUpdates.filter((j, i) =>
    approved.has(j.update_id || j.issue_key + j.field_to_update + i),
  ).length;

  const handleSyncApproved = async () => {
    if (!meetingContext?.accessToken || !meetingContext?.eventId) {
      toast.error('Missing context for Jira sync.');
      return;
    }
    const approvedIds = jiraUpdates
      .map((j, i) => ({ j, i }))
      .filter(({ j, i }) =>
        approved.has(j.update_id || j.issue_key + j.field_to_update + i),
      )
      .map(({ j }) => j.update_id)
      .filter(Boolean);

    if (approvedIds.length === 0) {
      toast.error('Approve at least one Jira update first.');
      return;
    }
    setIsSyncing(true);
    try {
      const res = await executeMeetingJiraSync(
        meetingContext.accessToken,
        meetingContext.eventId,
        meetingContext.isManual,
        approvedIds,
      );
      toast.success(
        `Synced ${res?.success_count ?? 0} of ${res?.attempted_count ?? 0} Jira updates.`,
      );
    } catch (err) {
      toast.error(err?.message || 'Failed to sync Jira updates.');
    } finally {
      setIsSyncing(false);
    }
  };

  if (jiraUpdates.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-zinc-500">
        No Jira updates found.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-xs text-zinc-500">
          <span className="font-bold text-green-400">{approvedCount}</span> of{' '}
          {jiraUpdates.length} approved
        </div>
        {approvedCount > 0 && meetingContext?.accessToken && (
          <button
            onClick={handleSyncApproved}
            disabled={isSyncing}
            className="flex items-center gap-1.5 rounded-lg bg-violet-500/15 px-3 py-1 text-xs font-semibold text-violet-400 transition-colors hover:bg-violet-500/25 disabled:opacity-50"
          >
            {isSyncing
              ? 'Syncing…'
              : `Apply ${approvedCount} Update${approvedCount > 1 ? 's' : ''} to Jira`}
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {jiraUpdates.map((item, idx) => {
          const itemId =
            item.update_id || item.issue_key + item.field_to_update + idx;
          const isExpanded = expanded === itemId;
          const isApproved = approved.has(itemId);

          return (
            <div
              key={itemId}
              className={`overflow-hidden rounded-lg border transition-colors ${
                isApproved
                  ? 'border-green-500/20 bg-green-500/5'
                  : 'border-zinc-700/20 bg-zinc-900/50'
              }`}
            >
              {/* eslint-disable-next-line */}
              <div
                onClick={() => setExpanded(isExpanded ? null : itemId)}
                className="flex cursor-pointer items-center gap-3 p-3.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <Badge
                      colorClass="text-violet-400"
                      bgClass="bg-violet-500/10"
                    >
                      {item.issue_key}
                    </Badge>
                    <span className="text-sm font-medium text-zinc-300">
                      Set{' '}
                      <span className="text-cyan-400">
                        {item.field_to_update}
                      </span>
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                    <span className="line-through">
                      {item.source_context?.substring(0, 30) || 'Current'}
                    </span>
                    <span>→</span>
                    <Badge colorClass="text-cyan-300" bgClass="bg-cyan-500/10">
                      {item.suggested_value?.substring(0, 30)}
                    </Badge>
                    <span className="text-zinc-600">·</span>
                    <span>{item.owner_email || 'Auto'}</span>
                  </div>
                </div>
                {/* eslint-disable-next-line */}
                <div onClick={(e) => e.stopPropagation()}>
                  <ApproveButton
                    id={itemId}
                    approvedSet={approved}
                    onApprove={onApprove}
                    onReject={onReject}
                  />
                </div>
                <ChevronDown
                  className={`h-3.5 w-3.5 flex-shrink-0 text-zinc-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </div>
              {isExpanded && (
                <div className="border-t border-zinc-700/10 px-4 pb-4 pt-0">
                  <div className="space-y-1.5 pt-3 text-xs text-zinc-500">
                    <div>
                      <span className="font-medium text-zinc-400">Issue:</span>{' '}
                      {item.issue_key}
                    </div>
                    <div>
                      <span className="font-medium text-zinc-400">Field:</span>{' '}
                      {item.field_to_update}
                    </div>
                    <div>
                      <span className="font-medium text-zinc-400">Change:</span>{' '}
                      <span className="line-through">
                        {item.source_context || 'current'}
                      </span>
                      {' → '}
                      {editingId === itemId ? (
                        <span className="inline-flex items-center gap-1.5">
                          <input
                            type="text"
                            value={
                              editedValues[itemId] ??
                              String(item.suggested_value || '')
                            }
                            onChange={(e) =>
                              setEditedValues((prev) => ({
                                ...prev,
                                [itemId]: e.target.value,
                              }))
                            }
                            className="rounded border border-cyan-500/30 bg-zinc-800 px-2 py-0.5 text-xs text-cyan-300 outline-none focus:border-cyan-500/50"
                            style={{ minWidth: 120 }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (
                                !meetingContext?.accessToken ||
                                !meetingContext?.eventId
                              ) {
                                toast.error('Missing context for saving.');
                                return;
                              }
                              setIsSavingEdit(true);
                              try {
                                const patchFn = meetingContext.isManual
                                  ? patchUserConfiguredMeetingJiraUpdate
                                  : patchMeetingJiraUpdate;
                                await patchFn(
                                  meetingContext.accessToken,
                                  meetingContext.eventId,
                                  item.update_id || itemId,
                                  {
                                    suggested_value:
                                      editedValues[itemId] ??
                                      String(item.suggested_value || ''),
                                  },
                                );
                                toast.success('Value saved.');
                                setEditingId(null);
                              } catch (err) {
                                toast.error(
                                  err.message || 'Failed to save edit.',
                                );
                              } finally {
                                setIsSavingEdit(false);
                              }
                            }}
                            disabled={isSavingEdit}
                            className="cursor-pointer rounded bg-cyan-500/15 p-1 text-cyan-300 transition-colors hover:bg-cyan-500/25 disabled:opacity-50"
                          >
                            <Save className="h-3 w-3" />
                          </button>
                        </span>
                      ) : (
                        <span
                          role="button"
                          tabIndex={0}
                          className="group/val inline-flex cursor-pointer items-center gap-1 text-cyan-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingId(itemId);
                            setEditedValues((prev) => ({
                              ...prev,
                              [itemId]:
                                prev[itemId] ??
                                String(item.suggested_value || ''),
                            }));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation();
                              setEditingId(itemId);
                              setEditedValues((prev) => ({
                                ...prev,
                                [itemId]:
                                  prev[itemId] ??
                                  String(item.suggested_value || ''),
                              }));
                            }
                          }}
                        >
                          {editedValues[itemId] ??
                            String(item.suggested_value || '')}
                          <Pencil className="h-2.5 w-2.5 text-zinc-600 opacity-0 transition-opacity group-hover/val:opacity-100" />
                        </span>
                      )}
                    </div>
                    {item.owner_email && (
                      <div>
                        <span className="font-medium text-zinc-400">
                          Owner:
                        </span>{' '}
                        {item.owner_email}
                      </div>
                    )}
                    {item.source_context && (
                      <div className="mt-2 italic text-zinc-500">
                        "{item.source_context}"
                      </div>
                    )}
                  </div>
                  {isApproved && (
                    <div className="mt-2.5 text-xs italic text-green-400">
                      ✓ Approved — agent will update Jira automatically.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── FOLLOW-UP ITEMS TAB ────────────────────────────────────────────────────────

const FollowUpItemsTab = ({ data, meetingContext }) => {
  const [expanded, setExpanded] = useState(null);
  const [isScheduling, setIsScheduling] = useState(false);
  const [approvedFollowUps, setApprovedFollowUps] = useState(new Set());

  const followUps = useMemo(() => {
    return data?.follow_up_items || data?.all_follow_ups || [];
  }, [data]);

  const handleApproveFollowUp = (id) => {
    setApprovedFollowUps((prev) => {
      const next = new Set(prev);
      next.delete(id + '_rejected');
      next.add(id);
      return next;
    });
  };

  const handleRejectFollowUp = (id) => {
    setApprovedFollowUps((prev) => {
      const next = new Set(prev);
      next.delete(id);
      next.add(id + '_rejected');
      return next;
    });
  };

  const approvedCount = followUps.filter((_, i) =>
    approvedFollowUps.has(String(i)),
  ).length;

  if (followUps.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-zinc-500">
        No follow-up items found.
      </div>
    );
  }

  const handleSchedule = async () => {
    if (approvedCount === 0) {
      toast.error('Approve at least one follow-up first.');
      return;
    }
    try {
      setIsScheduling(true);
      const accessToken =
        meetingContext?.accessToken || localStorage.getItem('token');
      if (!accessToken) {
        toast.error('Authentication required to schedule reminders.');
        return;
      }
      const eventId = meetingContext?.eventId;
      if (!eventId) {
        toast.error('Meeting event ID missing.');
        return;
      }

      let res;
      if (meetingContext?.isManual) {
        res = await scheduleUserConfiguredMeetingFollowups(
          accessToken,
          eventId,
        );
      } else {
        res = await scheduleMeetingFollowups(accessToken, eventId);
      }

      toast.success(
        res?.message || 'Successfully scheduled follow-up reminders.',
      );
    } catch (err) {
      toast.error('Failed to schedule reminders.');
    } finally {
      setIsScheduling(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <div className="text-xs text-zinc-500">
          <span className="font-bold text-violet-400">{followUps.length}</span>{' '}
          temporal commitments extracted from this meeting
          {approvedCount > 0 && (
            <>
              {' '}
              ·{' '}
              <span className="font-bold text-green-400">
                {approvedCount}
              </span>{' '}
              approved
            </>
          )}
        </div>
        {approvedCount > 0 && meetingContext?.accessToken && (
          <button
            onClick={handleSchedule}
            disabled={isScheduling}
            className="flex items-center gap-2 rounded-lg bg-violet-500/20 px-3 py-1.5 text-xs font-medium text-violet-400 hover:bg-violet-500/30 disabled:opacity-50"
          >
            {isScheduling
              ? 'Scheduling...'
              : `Schedule ${approvedCount} Follow-Up${approvedCount > 1 ? 's' : ''}`}
          </button>
        )}
      </div>
      <div className="flex flex-col gap-2">
        {followUps.map((item, idx) => {
          const itemId = String(idx);
          const isExpanded = expanded === idx;
          const isApproved = approvedFollowUps.has(itemId);
          const isRejected = approvedFollowUps.has(itemId + '_rejected');
          const urgency = getDateUrgency(
            item.follow_up_date || item.due_description,
          );
          return (
            <div
              key={idx}
              className={`overflow-hidden rounded-lg border transition-colors ${
                isApproved
                  ? 'border-green-500/20 bg-green-500/5'
                  : isRejected
                    ? 'border-red-500/15 bg-red-500/5'
                    : `${urgency?.border || 'border-zinc-700/20'} bg-zinc-900/50`
              }`}
              style={{
                borderLeftWidth: '3px',
                borderLeftColor: isApproved
                  ? '#22c55e'
                  : isRejected
                    ? '#ef4444'
                    : urgency
                      ? urgency.color.includes('red')
                        ? '#ef4444'
                        : urgency.color.includes('amber')
                          ? '#f59e0b'
                          : '#8b5cf6'
                      : '#8b5cf6',
              }}
            >
              {/* eslint-disable-next-line */}
              <div
                onClick={() => setExpanded(isExpanded ? null : idx)}
                className="flex cursor-pointer items-center gap-3 p-3.5"
              >
                <CalendarClock className="h-4 w-4 flex-shrink-0 text-violet-400" />
                <div className="min-w-0 flex-1">
                  <div className="mb-1 text-sm font-medium text-zinc-300">
                    {item.description}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                    <span className="flex items-center gap-1">
                      <ArrowUpRight className="h-3 w-3" />
                      {item.requester_email?.split('@')[0] || 'Unknown'}
                    </span>
                    <span className="text-zinc-600">→</span>
                    <span>
                      {item.responder_email?.split('@')[0] || 'Unknown'}
                    </span>
                    {item.due_description && (
                      <>
                        <span className="text-zinc-600">·</span>
                        <span className="text-violet-400">
                          {item.due_description}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                {item.follow_up_date && (
                  <DateBadge dateStr={item.follow_up_date} />
                )}
                {/* eslint-disable-next-line */}
                <div onClick={(e) => e.stopPropagation()}>
                  <ApproveButton
                    id={itemId}
                    approvedSet={approvedFollowUps}
                    onApprove={handleApproveFollowUp}
                    onReject={handleRejectFollowUp}
                  />
                </div>
                <ChevronDown
                  className={`h-3.5 w-3.5 flex-shrink-0 text-zinc-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </div>
              {isExpanded && (
                <div className="border-t border-zinc-700/10 px-4 pb-4 pl-11">
                  <div className="space-y-2 pt-3 text-xs text-zinc-500">
                    <div>
                      <span className="font-medium text-zinc-400">From:</span>{' '}
                      {item.requester_email || 'Unknown'}
                    </div>
                    <div>
                      <span className="font-medium text-zinc-400">To:</span>{' '}
                      {item.responder_email || 'Unknown'}
                    </div>
                    {item.due_description && (
                      <div>
                        <span className="font-medium text-zinc-400">Due:</span>{' '}
                        {item.due_description}
                      </div>
                    )}
                    {item.original_context && (
                      <div className="mt-2 space-y-1">
                        <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                          Source Quote
                        </div>
                        <p className="rounded border-l-2 border-violet-500/50 bg-zinc-800/50 p-2 text-xs italic text-zinc-500">
                          "{item.original_context}"
                        </p>
                      </div>
                    )}
                    {isApproved && (
                      <div className="mt-2 text-xs italic text-green-400">
                        ✓ Approved — will be scheduled when you submit.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── BLOCKERS TAB ──────────────────────────────────────────────────────────────

const BlockersTab = ({ data }) => {
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('all');
  const [notes, setNotes] = useState({});

  const blockers = useMemo(() => data?.unique_blockers || [], [data]);

  const types = useMemo(() => {
    const uniqueTypes = [
      ...new Set(
        blockers.map((b) => b.classification?.blocker_type).filter(Boolean),
      ),
    ];
    return ['all', ...uniqueTypes];
  }, [blockers]);

  const filtered = useMemo(() => {
    if (filter === 'all') return blockers;
    return blockers.filter((b) => b.classification?.blocker_type === filter);
  }, [blockers, filter]);

  if (blockers.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-zinc-500">
        No blockers found.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-1.5">
        {types.map((t) => (
          <button
            key={t}
            onClick={() => setFilter(t)}
            className={`cursor-pointer rounded-full border-none px-3 py-1 text-xs transition-all ${
              filter === t
                ? 'bg-red-500/15 font-semibold text-red-300'
                : 'bg-zinc-700/20 font-normal text-zinc-500 hover:bg-zinc-700/30'
            }`}
          >
            {t === 'all' ? 'All' : t}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        {filtered.map((item, idx) => {
          const itemId = item.blocker_uuid || idx;
          const isExpanded = expanded === itemId;
          const impactLevel = item.classification?.impact_level || 'medium';

          return (
            <div
              key={itemId}
              className={`overflow-hidden rounded-lg border bg-zinc-900/50 ${
                impactLevel === 'high'
                  ? 'border-red-500/20'
                  : impactLevel === 'medium'
                    ? 'border-amber-500/15'
                    : 'border-green-500/15'
              }`}
              style={{
                borderLeftWidth: '3px',
                borderLeftColor:
                  impactLevel === 'high'
                    ? '#ef4444'
                    : impactLevel === 'medium'
                      ? '#f59e0b'
                      : '#22c55e',
              }}
            >
              {/* eslint-disable-next-line */}
              <div
                onClick={() => setExpanded(isExpanded ? null : itemId)}
                className="flex cursor-pointer items-center gap-3 p-3.5"
              >
                <ImpactDot level={impactLevel} />
                <div className="min-w-0 flex-1">
                  <div className="mb-1 text-sm font-semibold text-zinc-200">
                    {item.representative_description}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {item.classification?.blocker_type && (
                      <Badge
                        colorClass="text-zinc-400"
                        bgClass="bg-zinc-500/10"
                      >
                        {item.classification.blocker_type}
                      </Badge>
                    )}
                    {item.classification?.suggested_owner_role && (
                      <span className="text-xs text-zinc-500">
                        Owner: {item.classification.suggested_owner_role}
                      </span>
                    )}
                    {item.blocking_issue_key && (
                      <span className="text-xs text-red-400">
                        Blocking: {item.blocking_issue_key}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronDown
                  className={`h-3.5 w-3.5 flex-shrink-0 text-zinc-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </div>
              {isExpanded && (
                <div className="border-t border-zinc-700/10 px-4 pb-4 pl-10">
                  <div className="space-y-3 pt-3 text-xs text-zinc-400">
                    {item.blocked_issue_keys?.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-zinc-500">Blocked issues:</span>
                        {item.blocked_issue_keys.map((key) => (
                          <Badge
                            key={key}
                            colorClass="text-red-400"
                            bgClass="bg-red-500/10"
                          >
                            {key}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {item.mentioned_by_emails?.length > 0 && (
                      <div>
                        <span className="font-medium text-zinc-500">
                          Mentioned by:
                        </span>{' '}
                        {item.mentioned_by_emails.join(', ')}
                      </div>
                    )}
                    {item.all_raw_mentions?.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                          Source Quotes
                        </div>
                        {item.all_raw_mentions.map((quote, i) => (
                          <p
                            key={i}
                            className="rounded border-l-2 border-zinc-600 bg-zinc-800/50 p-2 text-xs italic text-zinc-500"
                          >
                            "{quote}"
                          </p>
                        ))}
                      </div>
                    )}
                    {item.classification?.root_cause_hint && (
                      <div>
                        <span className="font-medium text-zinc-500">
                          Root Cause:
                        </span>{' '}
                        <span className="text-amber-300">
                          {item.classification.root_cause_hint}
                        </span>
                      </div>
                    )}
                    {item.classification?.impact_level && (
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-zinc-500">
                          Impact:
                        </span>
                        <Badge
                          colorClass={
                            impactLevel === 'high'
                              ? 'text-red-400'
                              : impactLevel === 'medium'
                                ? 'text-amber-400'
                                : 'text-green-400'
                          }
                          bgClass={
                            impactLevel === 'high'
                              ? 'bg-red-500/10'
                              : impactLevel === 'medium'
                                ? 'bg-amber-500/10'
                                : 'bg-green-500/10'
                          }
                        >
                          {item.classification.impact_level.toUpperCase()}
                        </Badge>
                      </div>
                    )}
                    <div className="mt-4 border-t border-zinc-700/30 pt-4">
                      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
                        Additional Context
                      </div>
                      <textarea
                        value={notes[itemId] || ''}
                        onChange={(e) =>
                          setNotes((prev) => ({
                            ...prev,
                            [itemId]: e.target.value,
                          }))
                        }
                        placeholder="Add notes about why this is blocked or what needs to happen..."
                        className="w-full resize-y rounded-lg border border-zinc-700/50 bg-zinc-900/50 p-2.5 text-xs text-zinc-300 placeholder-zinc-600 outline-none transition-colors focus:border-red-500/50 focus:bg-zinc-800"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── TASK TRACKER TAB ──────────────────────────────────────────────────────────

const TaskTrackerTab = ({ data }) => {
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('all');

  const tasks = useMemo(() => data?.task_wise_summary || [], [data]);

  const statuses = useMemo(() => {
    const uniqueStatuses = [
      ...new Set(tasks.map((t) => t.jira_details?.status).filter(Boolean)),
    ];
    return ['all', ...uniqueStatuses];
  }, [tasks]);

  const filtered = useMemo(() => {
    if (filter === 'all') return tasks;
    return tasks.filter((t) => t.jira_details?.status === filter);
  }, [tasks, filter]);

  const priorityColors = {
    Highest: 'text-red-400',
    High: 'text-orange-400',
    Medium: 'text-yellow-400',
    Low: 'text-green-400',
  };

  const statusColors = {
    'In Progress': { text: 'text-blue-400', bg: 'bg-blue-500/15' },
    'In Review': { text: 'text-violet-400', bg: 'bg-violet-500/15' },
    'To Do': { text: 'text-slate-400', bg: 'bg-slate-500/10' },
    Done: { text: 'text-green-400', bg: 'bg-green-500/10' },
  };

  if (tasks.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-zinc-500">
        No tasks found.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-1.5">
        {statuses.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`cursor-pointer rounded-full border-none px-3 py-1 text-xs transition-all ${
              filter === s
                ? 'bg-indigo-500/15 font-semibold text-indigo-300'
                : 'bg-zinc-700/20 font-normal text-zinc-500 hover:bg-zinc-700/30'
            }`}
          >
            {s === 'all'
              ? `All (${tasks.length})`
              : `${s} (${tasks.filter((t) => t.jira_details?.status === s).length})`}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-1.5">
        {filtered.map((item, idx) => {
          const isExpanded = expanded === item.issue_key;
          const statusStyle =
            statusColors[item.jira_details?.status] || statusColors['To Do'];
          const priorityColor =
            priorityColors[item.jira_details?.priority] || 'text-zinc-400';

          return (
            // eslint-disable-next-line
            <div
              key={item.issue_key || idx}
              className={`overflow-hidden rounded-lg border bg-zinc-900/50 ${
                item.is_blocked_in_meeting
                  ? 'border-orange-500/20'
                  : 'border-zinc-700/20'
              }`}
            >
              {/* eslint-disable-next-line */}
              <div
                onClick={() => setExpanded(isExpanded ? null : item.issue_key)}
                className="flex cursor-pointer items-center gap-3 p-3"
              >
                {item.is_blocked_in_meeting && <ImpactDot level="high" />}
                {!item.is_blocked_in_meeting && <div className="h-2 w-2" />}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="flex-shrink-0 font-mono text-xs font-bold text-indigo-400">
                      {item.issue_key}
                    </span>
                    <span className="truncate text-sm text-zinc-300">
                      {item.jira_details?.summary ||
                        item.consolidated_summary ||
                        'No summary'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  {item.is_blocked_in_meeting && (
                    <Badge
                      colorClass="text-orange-400"
                      bgClass="bg-orange-500/10"
                    >
                      Blocked
                    </Badge>
                  )}
                  <Badge colorClass={statusStyle.text} bgClass={statusStyle.bg}>
                    {item.jira_details?.status || 'Unknown'}
                  </Badge>
                  <span className={`text-xs font-bold ${priorityColor}`}>
                    {item.jira_details?.priority || ''}
                  </span>
                </div>
                <ChevronDown
                  className={`h-3.5 w-3.5 flex-shrink-0 text-zinc-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </div>
              {isExpanded && (
                <div className="border-t border-zinc-700/10 px-4 pb-3.5 pl-8">
                  <div className="flex flex-wrap gap-4 pt-2.5 text-xs text-zinc-500">
                    {item.jira_details?.issuetype && (
                      <div>
                        <span className="font-medium text-zinc-400">Type:</span>{' '}
                        {item.jira_details.issuetype}
                      </div>
                    )}
                    {item.jira_details?.assignee_name && (
                      <div>
                        <span className="font-medium text-zinc-400">
                          Assignee:
                        </span>{' '}
                        {item.jira_details.assignee_name}
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-zinc-400">
                        Priority:
                      </span>{' '}
                      <span className={priorityColor}>
                        {item.jira_details?.priority || 'N/A'}
                      </span>
                    </div>
                    {item.is_blocked_in_meeting && (
                      <div className="text-orange-400">
                        ⚠ This issue has active blockers
                      </div>
                    )}
                  </div>
                  {item.consolidated_summary && (
                    <div className="mt-3 text-xs leading-relaxed text-zinc-400">
                      {item.consolidated_summary}
                    </div>
                  )}
                  {item.speaker_contributions?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                        Contributions
                      </div>
                      {item.speaker_contributions.map((c, i) => (
                        <div key={i} className="rounded bg-zinc-800/50 p-2">
                          <p className="text-xs font-semibold text-zinc-300">
                            {c.speaker_name}
                          </p>
                          <p className="text-xs italic text-zinc-500">
                            "{c.quote_or_summary}"
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  {item.dependencies?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                        Dependencies
                      </div>
                      <div className="flex flex-col gap-1.5">
                        {item.dependencies.map((dep, di) => {
                          const typeColors = {
                            JIRA_ISSUE: {
                              text: 'text-blue-400',
                              bg: 'bg-blue-500/10',
                            },
                            EXTERNAL_TEAM: {
                              text: 'text-violet-400',
                              bg: 'bg-violet-500/10',
                            },
                            CLIENT_INPUT: {
                              text: 'text-amber-400',
                              bg: 'bg-amber-500/10',
                            },
                            ASSET: {
                              text: 'text-cyan-400',
                              bg: 'bg-cyan-500/10',
                            },
                          };
                          const style = typeColors[dep.type] || {
                            text: 'text-zinc-400',
                            bg: 'bg-zinc-500/10',
                          };
                          return (
                            <div
                              key={di}
                              className="flex items-center gap-2 text-xs"
                            >
                              <Badge colorClass={style.text} bgClass={style.bg}>
                                {(dep.type || 'UNKNOWN').replace(/_/g, ' ')}
                              </Badge>
                              <span className="text-zinc-400">
                                {dep.description}
                              </span>
                              {dep.linked_issue_key && (
                                <span className="text-blue-400">
                                  {dep.linked_issue_key}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── REPORT TAB (Use Case 5: Sprint Health Report) ─────────────────────────────

const RAG_CONFIG = {
  red: {
    label: 'At Risk',
    bg: 'bg-red-500/10',
    border: 'border-red-500/25',
    text: 'text-red-400',
    dot: 'bg-red-500',
    badge: 'bg-red-500/15 text-red-400',
  },
  amber: {
    label: 'Needs Attention',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    text: 'text-amber-400',
    dot: 'bg-amber-500',
    badge: 'bg-amber-500/15 text-amber-400',
  },
  green: {
    label: 'On Track',
    bg: 'bg-emerald-500/8',
    border: 'border-emerald-500/15',
    text: 'text-emerald-400',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-500/15 text-emerald-400',
  },
};

const computeRAG = (task, blockerIndex) => {
  const blockers = blockerIndex[task.issue_key] || [];
  const hasHighImpact = blockers.some((b) => b.impact_level === 'high');
  const hasMediumImpact = blockers.some((b) => b.impact_level === 'medium');
  const reasons = [];

  if (task.is_blocked_in_meeting && hasHighImpact) {
    reasons.push(
      `High-impact blocker: ${blockers.find((b) => b.impact_level === 'high')?.description}`,
    );
    return { rag: 'red', reasons };
  }
  if (task.escalation_needed) {
    reasons.push('Escalation flagged in meeting');
    return { rag: 'red', reasons };
  }
  if (task.is_blocked_in_meeting) {
    reasons.push(
      hasMediumImpact
        ? `Medium-impact blocker: ${blockers.find((b) => b.impact_level === 'medium')?.description}`
        : 'Blocked in meeting (low impact)',
    );
    return { rag: 'amber', reasons };
  }
  if (task.sentiment === 'negative') {
    reasons.push('Negative sentiment in meeting discussion');
    return { rag: 'amber', reasons };
  }
  if (
    task.jira_details?.status === 'To Do' &&
    (task.jira_details?.priority === 'High' ||
      task.jira_details?.priority === 'Highest')
  ) {
    reasons.push(
      `High-priority task not started (${task.jira_details.priority})`,
    );
    return { rag: 'amber', reasons };
  }
  reasons.push(
    task.sentiment === 'positive'
      ? 'Positive progress reported'
      : 'No blockers or risks identified',
  );
  return { rag: 'green', reasons };
};

const ReportTab = ({ data }) => {
  const tasks = useMemo(() => data?.task_wise_summary || [], [data]);
  const uniqueBlockers = useMemo(() => data?.unique_blockers || [], [data]);

  // Build issue_key → blockers map for RAG computation
  const blockerIndex = useMemo(() => {
    const idx = {};
    uniqueBlockers.forEach((b) => {
      const desc = b.representative_description || '';
      const impact = b.classification?.impact_level || 'medium';
      (b.blocked_issue_keys || []).forEach((key) => {
        (idx[key] = idx[key] || []).push({
          description: desc,
          impact_level: impact,
        });
      });
    });
    return idx;
  }, [uniqueBlockers]);

  const ragResults = useMemo(
    () => tasks.map((t) => ({ task: t, ...computeRAG(t, blockerIndex) })),
    [tasks, blockerIndex],
  );

  const counts = useMemo(
    () => ({
      red: ragResults.filter((r) => r.rag === 'red').length,
      amber: ragResults.filter((r) => r.rag === 'amber').length,
      green: ragResults.filter((r) => r.rag === 'green').length,
    }),
    [ragResults],
  );

  const overallHealth =
    counts.red > 0 ? 'red' : counts.amber > 0 ? 'amber' : 'green';
  const overallLabels = {
    red: 'Critical',
    amber: 'Needs Review',
    green: 'Healthy',
  };

  const copyReport = () => {
    const lines = [
      '# Sprint Health Report',
      `Generated: ${new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}`,
      `Overall Health: ${overallLabels[overallHealth]} | ${counts.red} RED · ${counts.amber} AMBER · ${counts.green} GREEN`,
      '',
    ];
    ['red', 'amber', 'green'].forEach((rag) => {
      const group = ragResults.filter((r) => r.rag === rag);
      if (group.length === 0) return;
      const emoji = rag === 'red' ? '🔴' : rag === 'amber' ? '🟡' : '🟢';
      lines.push(
        `## ${emoji} ${RAG_CONFIG[rag].label.toUpperCase()} (${group.length})`,
      );
      group.forEach(({ task, reasons }) => {
        const summary =
          task.jira_details?.summary ||
          task.consolidated_summary ||
          'No summary';
        lines.push(`- ${task.issue_key}: ${summary}`);
        reasons.forEach((r) => lines.push(`  → ${r}`));
      });
      lines.push('');
    });
    navigator.clipboard.writeText(lines.join('\n'));
    toast.success('Report copied to clipboard!');
  };

  if (tasks.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-zinc-500">
        No task data available to generate a report.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-zinc-200">
            Sprint Health Report
          </h3>
          <p className="mt-0.5 text-xs text-zinc-500">
            Red / Amber / Green status per issue based on meeting discussion
          </p>
        </div>
        <button
          onClick={copyReport}
          className="flex items-center gap-1.5 rounded-lg border border-zinc-700/50 bg-zinc-800/50 px-3 py-1.5 text-xs font-medium text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-200"
        >
          <Clipboard className="h-3.5 w-3.5" />
          Copy Report
        </button>
      </div>

      {/* Overall health banner */}
      <div
        className={`flex items-center gap-4 rounded-xl border p-4 ${RAG_CONFIG[overallHealth].bg} ${RAG_CONFIG[overallHealth].border}`}
      >
        <div
          className={`h-4 w-4 rounded-full ${RAG_CONFIG[overallHealth].dot}`}
        />
        <div>
          <p className={`text-sm font-bold ${RAG_CONFIG[overallHealth].text}`}>
            Overall: {overallLabels[overallHealth]}
          </p>
          <p className="text-xs text-zinc-500">
            {counts.red} at risk · {counts.amber} need attention ·{' '}
            {counts.green} on track
          </p>
        </div>
        <div className="ml-auto flex gap-3">
          {['red', 'amber', 'green'].map((rag) => (
            <div key={rag} className="text-center">
              <p
                className={`text-2xl font-extrabold leading-none ${RAG_CONFIG[rag].text}`}
              >
                {counts[rag]}
              </p>
              <p className="mt-0.5 text-[10px] uppercase tracking-widest text-zinc-500">
                {rag === 'red' ? 'Red' : rag === 'amber' ? 'Amber' : 'Green'}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Per-issue RAG list */}
      {['red', 'amber', 'green'].map((rag) => {
        const group = ragResults.filter((r) => r.rag === rag);
        if (group.length === 0) return null;
        const cfg = RAG_CONFIG[rag];
        return (
          <div key={rag}>
            <div className="mb-2 flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
              <h4
                className={`text-xs font-bold uppercase tracking-widest ${cfg.text}`}
              >
                {cfg.label} ({group.length})
              </h4>
            </div>
            <div className="flex flex-col gap-1.5">
              {group.map(({ task, reasons }) => {
                const summary =
                  task.jira_details?.summary ||
                  task.consolidated_summary ||
                  'No summary';
                const status = task.jira_details?.status;
                const assignee = task.jira_details?.assignee_name;
                return (
                  <div
                    key={task.issue_key}
                    className={`rounded-lg border px-4 py-3 ${cfg.bg} ${cfg.border}`}
                    style={{ borderLeftWidth: '3px' }}
                  >
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex-shrink-0 font-mono text-xs font-bold text-indigo-400">
                        {task.issue_key}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-zinc-300">
                          {summary}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          {status && (
                            <span className="rounded bg-zinc-700/40 px-1.5 py-0.5 text-[10px] text-zinc-400">
                              {status}
                            </span>
                          )}
                          {assignee && (
                            <span className="text-[11px] text-zinc-500">
                              {assignee}
                            </span>
                          )}
                          {task.jira_details?.priority && (
                            <span className="text-[11px] text-zinc-500">
                              {task.jira_details.priority}
                            </span>
                          )}
                        </div>
                        <ul className="mt-1.5 space-y-0.5">
                          {reasons.map((r, i) => (
                            <li
                              key={i}
                              className={`text-xs ${cfg.text} opacity-80`}
                            >
                              → {r}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <span
                        className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${cfg.badge}`}
                      >
                        {rag.toUpperCase()}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Action items needing attention */}
      {(data?.extracted_action_items || []).length > 0 && (
        <div className="rounded-xl border border-zinc-700/20 bg-zinc-900/40 p-4">
          <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-zinc-500">
            Open Action Items ({data.extracted_action_items.length})
          </h4>
          <div className="space-y-1">
            {data.extracted_action_items.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-zinc-500" />
                <span className="flex-1 truncate text-zinc-400">
                  {item.description}
                </span>
                {item.owner_email && (
                  <span className="flex-shrink-0 text-zinc-600">
                    {item.owner_email.split('@')[0]}
                  </span>
                )}
                {item.due_description && (
                  <span className="flex-shrink-0 text-violet-400">
                    {item.due_description}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────

export const ProcessedContent = ({ data, meetingContext }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [approved, setApproved] = useState(new Set());

  // Listen for the "Sprint Report" header button event
  useEffect(() => {
    const handler = (e) => setActiveTab(e.detail || 'report');
    window.addEventListener('meeting:open-tab', handler);
    return () => window.removeEventListener('meeting:open-tab', handler);
  }, []);

  const handleApprove = (id) => {
    setApproved((prev) => {
      const next = new Set(prev);
      next.delete(id + '_rejected');
      next.add(id);
      return next;
    });
  };

  const handleReject = (id) => {
    setApproved((prev) => {
      const next = new Set(prev);
      next.delete(id);
      next.add(id + '_rejected');
      return next;
    });
  };

  // Compute counts for tabs
  const jiraUpdatesCount = useMemo(() => {
    const tasks = data?.task_wise_summary || [];
    let count = 0;
    tasks.forEach((task) => {
      if (task.proposed_jira_issue_updates?.length > 0) {
        count += task.proposed_jira_issue_updates.length;
      }
    });
    return count;
  }, [data]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    {
      id: 'actions',
      label: 'Action Items',
      icon: CheckSquare,
      count: data?.extracted_action_items?.length || 0,
    },
    {
      id: 'jira',
      label: 'Jira Updates',
      icon: GitPullRequest,
      count: jiraUpdatesCount,
    },
    {
      id: 'followups',
      label: 'Follow-Ups',
      icon: CalendarClock,
      count: (data?.follow_up_items || data?.all_follow_ups || []).length,
    },
    {
      id: 'blockers',
      label: 'Blockers',
      icon: AlertOctagon,
      count: data?.unique_blockers?.length || 0,
    },
    {
      id: 'tasks',
      label: 'Tasks',
      icon: Target,
      count: data?.task_wise_summary?.length || 0,
    },
    {
      id: 'report',
      label: 'Report',
      icon: BarChart3,
    },
  ];

  if (!data) return null;

  return (
    <div className="flex min-w-0 flex-col">
      {/* Tabs */}
      <div className="custom-scrollbar mb-5 overflow-x-auto rounded-xl border border-zinc-700/20 bg-zinc-900/80 p-1">
        <div className="flex min-w-max gap-0.5">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border-none px-3 py-2 text-xs transition-all md:flex-1 ${
                  isActive
                    ? 'bg-blue-500/15 font-bold text-blue-300'
                    : 'font-medium text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
                {tab.count !== undefined && (
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                      isActive
                        ? 'bg-blue-500/25 text-blue-300'
                        : 'bg-zinc-700/30 text-zinc-500'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="min-w-0">
        {activeTab === 'overview' && <OverviewTab data={data} />}
        {activeTab === 'actions' && (
          <ActionItemsTab
            data={data}
            approved={approved}
            onApprove={handleApprove}
            onReject={handleReject}
            meetingContext={meetingContext}
          />
        )}
        {activeTab === 'jira' && (
          <JiraUpdatesTab
            data={data}
            approved={approved}
            onApprove={handleApprove}
            onReject={handleReject}
            meetingContext={meetingContext}
          />
        )}
        {activeTab === 'followups' && (
          <FollowUpItemsTab data={data} meetingContext={meetingContext} />
        )}
        {activeTab === 'blockers' && <BlockersTab data={data} />}
        {activeTab === 'tasks' && <TaskTrackerTab data={data} />}
        {activeTab === 'report' && <ReportTab data={data} />}
      </div>
    </div>
  );
};
